import type {
  Message,
  PendingMessage,
  SubmitMessageInput,
  SubmitMessageResponse,
  AdminActionResponse,
  ApiError,
  MessagesListResponse,
  PendingMessagesResponse,
} from '@shared/types';

export class ApiClientError extends Error {
  status: number;
  code: string | undefined;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = code;
  }
}

async function jsonFetch<T>(url: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(url, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(init.headers ?? {}),
    },
  });
  let payload: unknown = null;
  try {
    payload = await res.json();
  } catch {
    // empty body
  }
  if (!res.ok) {
    const err = (payload ?? {}) as Partial<ApiError>;
    throw new ApiClientError(err.error ?? `${res.status} ${res.statusText}`, res.status, err.code);
  }
  return payload as T;
}

export const apiClient = {
  /**
   * POST /api/messages — soumission publique d'un message.
   * Si `input.photoDataUrl` est fournie (data URL `data:image/(jpeg|png|webp);base64,...`),
   * le worker valide + uploade en R2 + stocke l'URL relative dans la DB.
   */
  async submitMessage(input: SubmitMessageInput): Promise<SubmitMessageResponse> {
    return jsonFetch<SubmitMessageResponse>('/api/messages', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  async getMessages(since?: number): Promise<MessagesListResponse> {
    const qs = since ? `?since=${encodeURIComponent(String(since))}` : '';
    return jsonFetch<MessagesListResponse>(`/api/messages${qs}`);
  },

  async getPending(adminKey: string): Promise<PendingMessage[]> {
    const res = await jsonFetch<PendingMessagesResponse>('/api/admin/messages?status=pending', {
      headers: { 'X-Admin-Key': adminKey },
    });
    return res.messages;
  },

  async verifyMessage(adminKey: string, id: string): Promise<AdminActionResponse> {
    return jsonFetch<AdminActionResponse>('/api/admin/verify', {
      method: 'POST',
      headers: { 'X-Admin-Key': adminKey },
      body: JSON.stringify({ id }),
    });
  },

  async rejectMessage(adminKey: string, id: string): Promise<AdminActionResponse> {
    return jsonFetch<AdminActionResponse>('/api/admin/reject', {
      method: 'POST',
      headers: { 'X-Admin-Key': adminKey },
      body: JSON.stringify({ id }),
    });
  },

  /**
   * Génère une image PNG depuis une scene textuelle (mode marthe).
   * Le worker call le provider sélectionné (Gemini Nano Banana ou OpenAI
   * gpt-image-1) puis cache en R2 ; ici on récupère les bytes et on retourne
   * une data URL `data:image/png;base64,...` qui est ensuite dispatchée dans
   * `params.imageSrc` côté store. Le pipeline image-to-ASCII existant
   * (`imageToAsciiGrid`) consomme cette data URL.
   *
   * @param provider - 'gemini' (default) ou 'openai'. Optional pour
   *   backward-compat — si omis, le worker utilisera 'gemini'.
   * @returns data URL `data:image/png;base64,...`
   * @throws ApiClientError si la requête échoue (turnstile, rate limit,
   *   provider non configuré, erreur upstream)
   */
  async generateScene(
    scene: string,
    turnstileToken: string,
    provider?: 'gemini' | 'openai',
    userApiKey?: string,
  ): Promise<string> {
    const body: Record<string, unknown> = {
      scene,
      turnstileToken,
      website: '',
    };
    if (provider) body.provider = provider;
    if (userApiKey && userApiKey.trim().length > 0) body.userApiKey = userApiKey.trim();
    const res = await fetch('/api/generate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'image/png, application/json',
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      // Worker renvoie JSON pour les erreurs
      let payload: Partial<ApiError> = {};
      try {
        payload = (await res.json()) as Partial<ApiError>;
      } catch {
        // empty body
      }
      throw new ApiClientError(
        payload.error ?? `${res.status} ${res.statusText}`,
        res.status,
        payload.code,
      );
    }
    // Body = PNG bytes
    const buffer = await res.arrayBuffer();
    const base64 = arrayBufferToBase64(buffer);
    return `data:image/png;base64,${base64}`;
  },
};

/**
 * Encode un ArrayBuffer en base64 (browser-side). Pas de Buffer.
 * Pour de petits PNG (< 2 Mo) c'est largement assez rapide.
 */
function arrayBufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let binary = '';
  // chunks pour éviter "Maximum call stack size exceeded" sur gros payload
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    const slice = bytes.subarray(i, i + CHUNK);
    binary += String.fromCharCode(...slice);
  }
  return btoa(binary);
}

export type { Message, PendingMessage, SubmitMessageInput };
